import WidgetConfig from "helpers/init/WidgetConfig";
import { _WINDOW_ } from "constants/dom";
import { APP_VERSION, EVENTS_PREFIX } from "core/config";
import { ITCFGlobalVendorList } from "types/tcf";
import convertSemanticVersion from "helpers/tcf/tcfHelpers/TCFConvertSemanticVersion";
import { ITCFConsentState } from "helpers/tcf/TCFConsentProvider/types";
import {
  TCF_CMP_ID,
  TCF_POLICY_VERSION,
  TCF_SUPPORTED_API_VERSION,
} from "helpers/tcf/constantsTCF";
import { UNDEFINED, FALSE, TRUE } from "constants/primitives";
import { getTcData, ITcDataReturn } from "helpers/tcf/getTcData";
import { TCFCallbackFunctions } from "./TCFApi";

class CookieFirstTCFApi {
  _dep: TCFApiDependencies;
  appVersion: number;

  constructor(deps: TCFApiDependencies) {
    // Assign dependencies so they can be used across entire class.
    this._dep = deps;
    this.appVersion = convertSemanticVersion(APP_VERSION);

    // Assign public endpoint in a way TCF require it
    _WINDOW_.CF__tcfapi = this.publicEndpoint.bind(this);

    // Dispatch event init if that was first render
    if (!deps.isPublicEndpointInit) {
      deps.setPublicEndpointInitialized(true);
    }
  }

  /**
   * That's the only public function, it works as main router, initializing other functions depends of what will be passed in parameters.
   */
  publicEndpoint(
    command: string,
    version: number,
    callback: TCFallCallbacks,
    parameter: number | string | undefined,
    callID: string | number
  ) {
    switch (command) {
      case "ping":
        this.ping(version, callback as TCFPingCallback);
        break;

      case "addEventListener":
        this.addEventListener(
          version,
          callback as TCFAddEventListenerCallback,
          parameter || callID
        );
        break;

      case "removeEventListener":
        if (parameter) {
          this.removeEventListener(
            callback as TCFRemoveListenerCallback,
            parameter
          );

          // I honestly don't remember why callback is being called here for the 2nd time, i commented it as it seems useless, removeEventListener call callback anyway, but i left it here just in case it's gonna be needed in the future.
          // callback()
        } else {
          console.error("No listenerID provided");
        }

        break;
    }
  }

  /**
   * Return ping status, with information about current state of cmp
   * @param version {string}
   * @param callback {function}
   */
  ping(version: number, callback: TCFPingCallback) {
    // Check if cmp is already loaded is in separate variable, because multiple fields require this information.
    const isLoaded =
      window.dataLayer &&
      window.dataLayer.findIndex((x) => x.event === `${EVENTS_PREFIX}_init`) >=
        0;
    const PingReturn: Partial<IPingReturn> = {
      /**
       * true - GDPR Applies
       * false - GDPR Does not apply
       * undefined - unknown whether GDPR Applies
       * see the section: "What does the gdprApplies value mean?"
       */
      gdprApplies: this._dep.widgetConfig.tcfGdprApplies,

      /**
       * true - CMP main script is loaded
       * false - still running stub
       */
      // FROM DEVELOPER:
      // I check only first element in dataLayer because if the app is initialized correctly, the event init should be at first place, and i want to make it run faster, so no loop
      cmpLoaded: isLoaded,
      cmpStatus: isLoaded ? "loaded" : "loading",

      /**
       * see Ping Status Codes in following table. There is also 3rd status code: 'disabled' for cases when GDPR does not apply, or the consent is current.
       * TODO: Add 3rd status
       */
      displayStatus: _WINDOW_.CF_visiblity ? "visible" : "hidden",

      /**
       * version of the CMP API that is supported, e.g. "2.0"
       */
      apiVersion: TCF_SUPPORTED_API_VERSION,

      /**
       * CMPs own/internal version that is currently running
       * undefined if still the stub
       * TCF require version to be number so we need to convert our "major.minor.patch" to number, so the result will be:
       * minor.patch in order to make it possible to distinguish patch from minor if one will be 1 number and other will be 2 numbers.
       */
      cmpVersion: this.appVersion,

      /**
       * IAB Assigned CMP ID
       * undefined if still the stub
       *
       * IMPORTANT: From what we know, we only get the ID once we submit correct working api
       */
      cmpId: TCF_CMP_ID,

      /**
       * Version of the GVL currently loaded by the CMP
       * undefined if still the stub
       */
      gvlVersion: this._dep.gvl ? this._dep.gvl.vendorListVersion : UNDEFINED,

      /**
       * Number of the supported TCF version
       * undefined if still the stub
       * 4 is the newest one, at the moment of implementation (july 2023)
       */
      tcfPolicyVersion: TCF_POLICY_VERSION,
    };

    if (callback && typeof callback === "function") {
      callback(PingReturn, TRUE);
    }

    return PingReturn;
  }

  addEventListener(
    version: number,
    callback: TCFAddEventListenerCallback,
    listenerID: number | string
  ) {
    if (callback && typeof callback === "function") {
      // And add event listener to listen to consent changes
      this._dep.setCallbackFunctions((prevState: TCFCallbackFunctions) => ({
        ...prevState,
        [listenerID]: callback,
      }));

      getTcData(
        listenerID.toString(),
        this._dep.consent,
        this._dep.widgetConfig,
        this._dep.gvl,
        this._dep.userLang
      )
        .then((TcData) => {
          // Init callback immediately because that's what IAB wants
          if (callback && typeof callback === "function") {
            callback(TcData, TRUE);
          }
        })
        .catch((error) => {
          console.error("Error getting TcData:", error);
        });
    }
  }

  removeEventListener(
    callback: TCFRemoveListenerCallback,
    listenerID: number | string
  ) {
    if (listenerID && listenerID in this._dep.callbackFunctions) {
      // Remove callback with given listenerID
      this._dep.setCallbackFunctions((prevState: TCFCallbackFunctions) => {
        const newState = { ...prevState };
        delete newState[listenerID];
        return newState;
      });

      // Check if there is callback and if it is a proper function
      if (callback && typeof callback === "function") {
        callback(TRUE);
      }

      return TRUE;
    } else {
      if (callback && typeof callback === "function") {
        callback(FALSE);
      }
      return FALSE;
    }
  }
}

export interface TCFApiDependencies {
  userLang: string;
  consent: ITCFConsentState;
  widgetConfig: WidgetConfig;
  stealthMode: boolean;
  callbackFunctions: TCFCallbackFunctions;
  setCallbackFunctions: React.Dispatch<
    React.SetStateAction<TCFCallbackFunctions>
  >;
  isPublicEndpointInit: boolean;
  setPublicEndpointInitialized: React.Dispatch<React.SetStateAction<boolean>>;
  gvl?: ITCFGlobalVendorList | undefined;
}

export type TCFPingCallback = (
  pingReturn: Partial<IPingReturn>,
  success: boolean
) => void;
export type TCFAddEventListenerCallback = (
  TcData: ITcDataReturn,
  success: boolean
) => void;
export type TCFRemoveListenerCallback = (success: boolean) => void;

export type TCFallCallbacks =
  | TCFAddEventListenerCallback
  | TCFRemoveListenerCallback
  | TCFPingCallback;
export interface IPingReturn {
  /**
   * true - GDPR Applies
   * false - GDPR Does not apply
   * undefined - unknown whether GDPR Applies
   * see the section: "What does the gdprApplies value mean?"
   */
  gdprApplies: boolean | undefined;

  /**
   * true - CMP main script is loaded
   * false - still running stub
   */
  cmpLoaded: boolean;

  /**
   * see Ping Status Codes in following table
   */
  cmpStatus: string;

  /**
   * see Ping Status Codes in following table
   */
  displayStatus: string;

  /**
   * version of the CMP API that is supported, e.g. "2.0"
   */
  apiVersion: string;

  /**
   * CMPs own/internal version that is currently running
   * undefined if still the stub
   */
  cmpVersion: number | undefined;

  /**
   * IAB Assigned CMP ID
   * undefined if still the stub
   */
  cmpId: number | undefined;

  /**
   * Version of the GVL currently loaded by the CMP
   * undefined if still the stub
   */
  gvlVersion: number | undefined;

  /**
   * Number of the supported TCF version
   * undefined if still the stub
   */
  tcfPolicyVersion: number | undefined;
}

export default CookieFirstTCFApi;
